import React, { useState, useCallback } from "react";
import { CustomerPreorderMenu } from "../../../../../../types/bookings";
import { EDSButton } from "../../../../../eds/EDSButton";
import { Trash, ChevronDown, ChevronUp } from "lucide-react";
import CustomerPreorderAdvancedOptions from "./CustomerPreorderAdvancedOptions";
import { ui } from "@workspace/mobile";

interface CustomerPreorderMenuRowProps {
  menu: CustomerPreorderMenu;
  menuName: string;
  menuIndex: number;
  onRemove: (menuIndex: number) => Promise<void>;
  onUpdate: (
    menuIndex: number,
    menuSettings: Partial<CustomerPreorderMenu>
  ) => Promise<void>;
  isSaving: boolean;
  numPeople?: number;
  currencySymbol?: string;
}

const CustomerPreorderMenuRow: React.FC<CustomerPreorderMenuRowProps> = ({
  menu,
  menuName,
  menuIndex,
  onRemove,
  onUpdate,
  isSaving,
  numPeople,
  currencySymbol
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleRemove = useCallback(async () => {
    const confirmDelete = await ui.displayConfirm({
      title: "Remove Menu?",
      message: `Are you sure you want to remove "${menuName}" from customer pre-orders?`,
      accept: "Remove",
      cancel: "Cancel"
    });

    if (confirmDelete) {
      await onRemove(menuIndex);
    }
  }, [menuName, menuIndex, onRemove]);

  const getPaymentLabel = (): string => {
    switch (menu.payment_required) {
      case "full_amount":
        return "Full amount";
      case "fixed_amount":
        return `Fixed: ${menu.payment_amount ?? 0}`;
      case "percentage":
        return `${menu.payment_percentage ?? 0}%`;
      default:
        return "None";
    }
  };

  const getWhoPaysLabel = (): string => {
    switch (menu.who_pays) {
      case "lead_booker":
        return "Lead booker";
      case "group_member":
        return "Group members";
      default:
        return "Not set";
    }
  };

  return (
    <div className="border border-border rounded-lg p-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium text-sm">{menuName}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Payment: {getPaymentLabel()} | Who pays: {getWhoPaysLabel()}
            {menu.hide_prices && " | Prices hidden"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EDSButton
            variant="outline"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            disabled={isSaving}
            aria-expanded={showAdvanced}
            aria-controls={`advanced-options-${menuIndex}`}
            aria-label={
              showAdvanced ? "Hide advanced options" : "Show advanced options"
            }
          >
            {showAdvanced ? (
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            )}
          </EDSButton>
          <EDSButton
            variant="outline"
            size="sm"
            onClick={handleRemove}
            disabled={isSaving}
            aria-label={`Remove ${menuName} from pre-orders`}
          >
            <Trash className="h-4 w-4" aria-hidden="true" />
          </EDSButton>
        </div>
      </div>

      {showAdvanced && (
        <div id={`advanced-options-${menuIndex}`} className="mt-3">
          <CustomerPreorderAdvancedOptions
            menu={menu}
            menuIndex={menuIndex}
            onUpdate={onUpdate}
            isSaving={isSaving}
            numPeople={numPeople}
            currencySymbol={currencySymbol}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerPreorderMenuRow;
